import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { waLink } from "../lib/shop";
import DashboardShell from "./DashboardShell";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, LineChart, Line, CartesianGrid,
} from "recharts";
import {
  LayoutDashboard, CalendarDays, CalendarRange, Scissors, UserCog, Users, Star, Image,
  Ticket, Clock, FileText, Loader2, Plus, Trash2, Check, X, PoundSterling, CalendarCheck,
  Bell, Upload, ChevronLeft, ChevronRight, Phone, Pencil, RefreshCw, MessageCircle, KeyRound, GripVertical,
  Ban, Mail, History, AlertTriangle, Calendar as CalIcon, Eye, EyeOff, ArrowUp, ArrowDown,
} from "lucide-react";

const NAV = [
  { key: "bookings", label: "Bookings", icon: CalendarCheck },
  { key: "calendar", label: "Calendar", icon: CalendarRange },
  { key: "availability", label: "Availability", icon: Clock },
  { key: "holidays", label: "Holidays & Time Off", icon: Ban },
  { key: "services", label: "Services", icon: Scissors },
  { key: "barber", label: "Barbers", icon: UserCog },
  { key: "customers", label: "Customers", icon: Users },
  { key: "reviews", label: "Reviews", icon: Star },
  { key: "gallery", label: "Gallery", icon: Image },
  { key: "content", label: "Website Content", icon: FileText },
];

const inputCls = "w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#D4AF37] focus:ring-0";
const ddmm = (s) => { if (!s) return ""; const [y, m, d] = s.split("-"); return `${d}/${m}/${y}`; };
const ddmmLong = (s) => { if (!s) return ""; const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" }); };
const Loader = () => <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" /></div>;
const backendURL = process.env.REACT_APP_BACKEND_URL;
const imgURL = (u) => (u && u.startsWith("/api/") ? `${backendURL}${u}` : u);

const downloadCsv = async (path, filename) => {
  try {
    const { data } = await api.get(path, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    toast.success("Export downloaded");
  } catch (e) { toast.error("Export failed"); }
};

const STATUS = {
  pending: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  confirmed: "bg-green-500/15 text-green-400 border-green-500/30",
  arrived: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  completed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  declined: "bg-red-500/15 text-red-400 border-red-500/30",
  "no-show": "bg-rose-500/15 text-rose-400 border-rose-500/30",
  cancelled: "bg-gray-500/15 text-gray-400 border-gray-500/30",
  "walk-in": "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/30",
};
const Badge = ({ s }) => <span className={`text-xs px-2.5 py-1 rounded-full border ${STATUS[s] || STATUS.cancelled}`}>{s}</span>;

export default function AdminDashboard() {
  const { barbers, staff, setStaff, staffLogout } = useAuth();
  const [active, setActive] = useState("bookings");
  if (staff && staff.must_change_password) return <ForcePasswordChange setUser={setStaff} />;
  return (
    <DashboardShell testid="admin-dashboard" title="Admin" nav={NAV} active={active} onNav={setActive} identity={staff} onLogout={staffLogout}>
      {active === "bookings" && <Bookings barbers={barbers} />}
      {active === "calendar" && <Calendar />}
      {active === "availability" && <Availability />}
      {active === "holidays" && <Holidays />}
      {active === "services" && <Services />}
      {active === "barber" && <BarbersManager />}
      {active === "customers" && <Customers />}
      {active === "reviews" && <Reviews />}
      {active === "gallery" && <GalleryAdmin />}
      {active === "content" && <Content />}
    </DashboardShell>
  );
}

// ---------------- Forced password change (first login security gate)
function ForcePasswordChange({ setUser }) {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) return toast.error("Passwords do not match");
    setSaving(true);
    try {
      const { data } = await api.post("/auth/change-password", { new_password: pw });
      setUser(data.user);
      toast.success("Password updated — welcome to your dashboard");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] px-5" data-testid="force-password-change">
      <form onSubmit={submit} className="w-full max-w-md bg-[#141414] border border-white/10 rounded-2xl p-8">
        <div className="flex items-center gap-2 text-[#D4AF37] mb-2"><KeyRound className="w-5 h-5" /><span className="label-caps">Secure your account</span></div>
        <h1 className="font-display text-3xl mb-2">Set a new password</h1>
        <p className="text-gray-400 text-sm mb-6">For security, please replace the default password before continuing. Use at least 8 characters with a letter and a number.</p>
        <div className="relative mb-3">
          <input type={show ? "text" : "password"} data-testid="new-password" placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} className={inputCls + " pr-12"} required />
          <button type="button" data-testid="new-password-toggle" onClick={() => setShow((v) => !v)} aria-label={show ? "Hide password" : "Show password"} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#D4AF37] transition-colors duration-200">
            {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
        <input type={show ? "text" : "password"} data-testid="confirm-password" placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls + " mb-5"} required />
        <button type="submit" data-testid="set-password-btn" disabled={saving} className="w-full py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#F3E5AB] transition-colors duration-300 disabled:opacity-50">{saving ? "Saving…" : "Set Password & Continue"}</button>
      </form>
    </div>
  );
}

// ---------------- Overview
function Overview() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/analytics").then((r) => setA(r.data)).catch(() => {}); }, []);
  if (!a) return <Loader />;
  const stats = [
    { label: "Today", value: a.today, icon: CalendarDays },
    { label: "Upcoming", value: a.upcoming, icon: CalendarCheck },
    { label: "Pending", value: a.pending, icon: Bell },
    { label: "Confirmed", value: a.confirmed, icon: Check },
    { label: "Completed", value: a.completed, icon: Check },
    { label: "Rejected", value: a.declined, icon: X },
    { label: "No-Shows", value: a.no_shows, icon: X },
    { label: "Cancelled", value: a.cancelled, icon: X },
    { label: "Total Bookings", value: a.total_appointments, icon: CalendarRange },
  ];
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="bg-[#141414] border border-white/5 rounded-2xl p-5">
            <s.icon className="w-5 h-5 text-[#D4AF37] mb-3" />
            <div className="font-display text-3xl">{s.value}</div>
            <div className="label-caps mt-1">{s.label}</div>
          </div>
        ))}
      </div>
      <RecentRequests />
      <TodaysAppointments />
    </div>
  );
}

// ---------------- Bookings
function Bookings({ barbers }) {
  const [items, setItems] = useState(null);
  const [services, setServices] = useState([]);
  const [filters, setFilters] = useState({ date: "", status: "pending", service_id: "", q: "" });
  const [modal, setModal] = useState(null); // {type, appt}
  const [showManual, setShowManual] = useState(false);
  const [page, setPage] = useState(1);
  const perPage = 12;

  const reqId = useRef(0);
  const load = useCallback(() => {
    const id = ++reqId.current;
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/appointments", { params }).then((r) => { if (id === reqId.current) setItems(r.data); }).catch(() => { if (id === reqId.current) setItems([]); });
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/services").then((r) => setServices(r.data)); }, []);

  const act = async (id, status, reason = "") => {
    try { await api.put(`/appointments/${id}/status`, { status, reason }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  // Default New Bookings view = future/current pending only (past pending hidden unless a date/other filter is chosen)
  const pendingDefault = filters.status === "pending" && !filters.date;
  const isFutureOrNow = (a) => new Date(`${a.date}T${a.start_time || "00:00"}:00`).getTime() >= Date.now();
  const view = items === null ? null : (pendingDefault ? items.filter(isFutureOrNow) : items);

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-6 items-end">
        <div><label className="label-caps block mb-1">Date</label><input type="date" data-testid="filter-date" value={filters.date} onChange={(e) => setFilters({ ...filters, date: e.target.value })} className={inputCls + " w-44"} /></div>
        <div><label className="label-caps block mb-1">Status</label>
          <select data-testid="filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className={inputCls + " w-40"}>
            <option value="">All</option>{["pending", "confirmed", "arrived", "completed", "declined", "no-show", "cancelled", "walk-in"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select></div>
        <div><label className="label-caps block mb-1">Service</label>
          <select value={filters.service_id} onChange={(e) => setFilters({ ...filters, service_id: e.target.value })} className={inputCls + " w-44"}>
            <option value="">All</option>{services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select></div>
        <div className="flex-1 min-w-[180px]"><label className="label-caps block mb-1">Search customer</label><input data-testid="filter-search" placeholder="Name or phone" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} className={inputCls} /></div>
        <button data-testid="export-bookings" onClick={() => { const p = new URLSearchParams(); Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); }); downloadCsv(`/appointments/export?${p.toString()}`, `bookings_${new Date().toISOString().slice(0, 10)}.csv`); }} className="px-5 py-3 rounded-full border border-white/15 text-gray-200 flex items-center gap-2 hover:border-[#D4AF37]/50 transition-colors duration-200"><FileText className="w-4 h-4" /> Export CSV</button>
        <button data-testid="manual-booking-btn" onClick={() => setShowManual(true)} className="px-5 py-3 rounded-full bg-[#D4AF37] text-black font-medium flex items-center gap-2 hover:bg-[#F3E5AB] transition-colors duration-300"><Plus className="w-4 h-4" /> Manual Booking</button>
      </div>

      {view === null ? <Loader /> : view.length === 0 ? <p className="text-gray-500 py-10 text-center">No bookings match these filters.</p> : (
        <div className="space-y-3">
          {view.slice((page - 1) * perPage, page * perPage).map((a) => (
            <div key={a.id} data-testid={`booking-${a.id}`} className="bg-[#141414] border border-white/5 rounded-2xl p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-4">
                  <div className="text-center w-16 shrink-0"><div className="font-display text-xl text-[#D4AF37]">{a.start_time}</div></div>
                  <div>
                    <div className="text-lg flex items-center gap-2 flex-wrap">{a.customer_name} <Badge s={a.status} />{a.customer_flagged && <span data-testid={`booking-flag-${a.id}`} title={a.flag_reason || "Repeat no-show"} className="text-[0.6rem] px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/30 uppercase tracking-wide">No-show{a.flag_reason ? `: ${a.flag_reason}` : ""}</span>}</div>
                    <div data-testid={`booking-date-long-${a.id}`} className="text-[#D4AF37]/90 text-sm font-medium mt-0.5">{ddmmLong(a.date)} · {a.start_time}</div>
                    <div className="text-gray-400 text-sm">{a.service_name} · {a.duration}min · £{a.price}</div>
                    <div className="text-[0.65rem] text-gray-600 mt-0.5">Ref {a.ref || ("WN-" + (a.id || "").slice(0, 6).toUpperCase())} · created {ddmm((a.created_at || "").slice(0, 10))}</div>
                    <div className="text-xs text-gray-500 mt-1 flex items-center gap-3 flex-wrap">
                      {a.customer_phone && <a href={`tel:${a.customer_phone}`} className="flex items-center gap-1 hover:text-[#D4AF37]"><Phone className="w-3 h-3" />{a.customer_phone}</a>}
                      {a.customer_phone && <a data-testid={`whatsapp-${a.id}`} href={waLink(a.customer_phone, `Hello ${a.customer_name}, this is Who's Next Barber Shop regarding your ${a.service_name} appointment on ${ddmm(a.date)} at ${a.start_time}.`)} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[#25D366] hover:underline"><MessageCircle className="w-3 h-3" /> Message</a>}
                      {a.customer_email && <a data-testid={`email-${a.id}`} href={`mailto:${a.customer_email}?subject=${encodeURIComponent("Your Who's Next appointment")}&body=${encodeURIComponent(`Hello ${a.customer_name}, regarding your ${a.service_name} appointment on ${ddmm(a.date)} at ${a.start_time}.`)}`} className="flex items-center gap-1 hover:text-[#D4AF37]"><Mail className="w-3 h-3" /> Email</a>}
                      {a.notes && <span className="italic">"{a.notes}"</span>}
                    </div>
                    {a.admin_notes && <div className="text-xs text-amber-400/80 mt-1" data-testid={`admin-notes-${a.id}`}>Internal: {a.admin_notes}</div>}
                    {a.no_show_reason && <div className="text-xs text-rose-400/80 mt-1">No-show reason: {a.no_show_reason}</div>}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {a.status === "pending" && <>
                  <ActBtn testid={`accept-${a.id}`} onClick={() => act(a.id, "confirmed")} tone="green"><Check className="w-4 h-4" /> Accept</ActBtn>
                  <ActBtn testid={`decline-${a.id}`} onClick={() => act(a.id, "declined")} tone="red"><X className="w-4 h-4" /> Decline</ActBtn>
                </>}
                {["confirmed", "walk-in", "arrived"].includes(a.status) && <>
                  {a.status !== "arrived" && <ActBtn onClick={() => act(a.id, "arrived")} tone="blue">Arrived</ActBtn>}
                  <ActBtn testid={`complete-${a.id}`} onClick={() => act(a.id, "completed")} tone="green"><Check className="w-4 h-4" /> Complete</ActBtn>
                  <ActBtn onClick={() => setModal({ type: "noshow", appt: a })} tone="gray">No-show</ActBtn>
                  <ActBtn onClick={() => act(a.id, "cancelled")} tone="gray">Cancel</ActBtn>
                </>}
                {["pending", "confirmed", "walk-in", "arrived"].includes(a.status) &&
                  <ActBtn testid={`reschedule-${a.id}`} onClick={() => setModal({ type: "reschedule", appt: a })} tone="gray"><RefreshCw className="w-4 h-4" /> Reschedule</ActBtn>}
                <ActBtn onClick={() => setModal({ type: "edit", appt: a })} tone="gray"><Pencil className="w-4 h-4" /> Edit</ActBtn>
              </div>
            </div>
          ))}
          {view.length > perPage && (
            <div className="flex items-center justify-center gap-4 pt-4" data-testid="pagination">
              <button disabled={page === 1} onClick={() => setPage(page - 1)} className="px-4 py-2 rounded-full bg-white/5 text-sm disabled:opacity-40">Prev</button>
              <span className="text-sm text-gray-400">Page {page} of {Math.ceil(view.length / perPage)}</span>
              <button disabled={page >= Math.ceil(view.length / perPage)} onClick={() => setPage(page + 1)} className="px-4 py-2 rounded-full bg-white/5 text-sm disabled:opacity-40">Next</button>
            </div>
          )}
        </div>
      )}

      {modal?.type === "noshow" && <NoShowModal appt={modal.appt} onClose={() => setModal(null)} onDone={(reason) => { act(modal.appt.id, "no-show", reason); setModal(null); }} />}
      {modal?.type === "reschedule" && <RescheduleModal appt={modal.appt} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
      {modal?.type === "edit" && <EditCustomerModal appt={modal.appt} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
      {showManual && <ManualModal barbers={barbers} services={services} onClose={() => setShowManual(false)} onDone={() => { setShowManual(false); load(); }} />}
    </div>
  );
}

const ActBtn = ({ children, onClick, tone, testid }) => {
  const tones = { green: "bg-green-500/10 text-green-400 hover:bg-green-500/20", red: "bg-red-500/10 text-red-400 hover:bg-red-500/20", blue: "bg-blue-500/10 text-blue-400 hover:bg-blue-500/20", gray: "bg-white/5 text-gray-300 hover:bg-white/10" };
  return <button data-testid={testid} onClick={onClick} className={`px-4 py-2 rounded-full text-sm flex items-center gap-1 transition-colors duration-200 ${tones[tone]}`}>{children}</button>;
};

const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
      <div className="flex justify-between items-center mb-5"><h3 className="font-display text-2xl">{title}</h3><button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button></div>
      {children}
    </div>
  </div>
);

function RescheduleModal({ appt, onClose, onDone }) {
  const [date, setDate] = useState(appt.date);
  const [time, setTime] = useState(appt.start_time);
  const save = async () => {
    try { await api.put(`/appointments/${appt.id}/reschedule`, { date, start_time: time }); toast.success("Rescheduled"); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return <Modal title="Reschedule" onClose={onClose}>
    <div className="space-y-4">
      <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} data-testid="resched-date" />
      <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className={inputCls} data-testid="resched-time" />
      <button onClick={save} data-testid="resched-save" className="w-full py-3 rounded-full bg-[#D4AF37] text-black font-medium">Save</button>
    </div>
  </Modal>;
}

function EditCustomerModal({ appt, onClose, onDone }) {
  const [form, setForm] = useState({ customer_name: appt.customer_name, customer_phone: appt.customer_phone || "", notes: appt.notes || "" });
  const [adminNotes, setAdminNotes] = useState(appt.admin_notes || "");
  const save = async () => {
    try {
      await api.put(`/appointments/${appt.id}/customer`, form);
      await api.put(`/appointments/${appt.id}/admin-notes`, { admin_notes: adminNotes });
      toast.success("Updated"); onDone();
    } catch (e) { toast.error(apiError(e)); }
  };
  return <Modal title={`Booking ${appt.ref || ""}`} onClose={onClose}>
    <div className="space-y-4">
      <input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Customer name" className={inputCls} />
      <input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} placeholder="Phone" className={inputCls} />
      <div><label className="label-caps block mb-1">Customer notes</label><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes from the customer" rows={2} className={inputCls} data-testid="edit-notes" /></div>
      <div><label className="label-caps block mb-1">Internal admin notes</label><textarea value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} placeholder="Private notes only staff can see" rows={2} className={inputCls} data-testid="edit-admin-notes" /></div>
      {appt.history?.length > 0 && (
        <div><div className="label-caps mb-1 flex items-center gap-1"><History className="w-3 h-3" /> Change history</div>
          <div className="space-y-1 max-h-32 overflow-y-auto text-xs text-gray-400" data-testid="booking-history">
            {appt.history.map((h, i) => <div key={i} className="border-b border-white/5 pb-1">{(h.at || "").slice(0, 16).replace("T", " ")} · {h.action} <span className="text-gray-600">({h.by})</span></div>)}
          </div>
        </div>
      )}
      <button onClick={save} data-testid="edit-save" className="w-full py-3 rounded-full bg-[#D4AF37] text-black font-medium">Save</button>
    </div>
  </Modal>;
}

function ManualModal({ barbers, services, onClose, onDone, initialDate }) {
  const [form, setForm] = useState({ service_id: "", barber_id: barbers[0]?.id || "", date: initialDate || new Date().toISOString().split("T")[0], start_time: "", customer_name: "", customer_phone: "", customer_email: "", status: "confirmed" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    try { await api.post("/appointments/manual", form); toast.success("Booking created"); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return <Modal title="Manual / Phone Booking" onClose={onClose}>
    <div className="space-y-3">
      <input data-testid="manual-name" placeholder="Customer name" value={form.customer_name} onChange={set("customer_name")} className={inputCls} />
      <input data-testid="manual-phone" placeholder="Phone" value={form.customer_phone} onChange={set("customer_phone")} className={inputCls} />
      <input data-testid="manual-email" placeholder="Email (optional)" value={form.customer_email} onChange={set("customer_email")} className={inputCls} />
      <select data-testid="manual-service" value={form.service_id} onChange={set("service_id")} className={inputCls}><option value="">Service</option>{services.map((s) => <option key={s.id} value={s.id}>{s.name} · £{s.price}</option>)}</select>
      <select value={form.barber_id} onChange={set("barber_id")} className={inputCls}>{barbers.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</select>
      <div className="grid grid-cols-2 gap-3"><input data-testid="manual-date" type="date" value={form.date} onChange={set("date")} className={inputCls} /><input data-testid="manual-time" type="time" value={form.start_time} onChange={set("start_time")} className={inputCls} /></div>
      <select value={form.status} onChange={set("status")} className={inputCls}><option value="confirmed">Confirmed</option><option value="walk-in">Walk-in</option><option value="arrived">Arrived</option></select>
      <button data-testid="manual-save" onClick={save} className="w-full py-3 rounded-full bg-[#D4AF37] text-black font-medium">Create Booking</button>
    </div>
  </Modal>;
}

// ---------------- Calendar
function Calendar() {
  const { barbers } = useAuth();
  const [services, setServices] = useState([]);
  const [showManual, setShowManual] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [view, setView] = useState("day");
  const [anchor, setAnchor] = useState(new Date());
  const [items, setItems] = useState([]);
  const iso = (d) => d.toISOString().split("T")[0];

  const range = () => {
    if (view === "day") return { from: iso(anchor), to: iso(anchor) };
    if (view === "week") {
      const s = new Date(anchor); s.setDate(anchor.getDate() - ((anchor.getDay() + 6) % 7));
      const e = new Date(s); e.setDate(s.getDate() + 6);
      return { from: iso(s), to: iso(e), start: s };
    }
    const s = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const e = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    return { from: iso(s), to: iso(e), start: s, end: e };
  };
  const r = range();
  const reqId = useRef(0);
  useEffect(() => {
    const id = ++reqId.current;
    api.get("/appointments", { params: { date_from: r.from, date_to: r.to } })
      .then((res) => { if (id === reqId.current) setItems(res.data); })
      .catch(() => { if (id === reqId.current) setItems([]); });
  }, [view, anchor, refresh]); // eslint-disable-line
  useEffect(() => { api.get("/services").then((res) => setServices(res.data)).catch(() => {}); }, []);

  const move = (dir) => {
    const d = new Date(anchor);
    if (view === "day") d.setDate(d.getDate() + dir);
    else if (view === "week") d.setDate(d.getDate() + dir * 7);
    else d.setMonth(d.getMonth() + dir);
    setAnchor(d);
  };
  const byDate = {};
  items.forEach((a) => { (byDate[a.date] = byDate[a.date] || []).push(a); });
  Object.values(byDate).forEach((l) => l.sort((a, b) => a.start_time.localeCompare(b.start_time)));

  const title = view === "month" ? anchor.toLocaleDateString("en-GB", { month: "long", year: "numeric" })
    : view === "week" ? `Week of ${r.start.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`
    : anchor.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => move(-1)} className="p-2 rounded-full bg-white/5 hover:bg-white/10"><ChevronLeft className="w-4 h-4" /></button>
          <span className="font-display text-2xl w-64 text-center">{title}</span>
          <button onClick={() => move(1)} className="p-2 rounded-full bg-white/5 hover:bg-white/10"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1 bg-[#141414] rounded-full p-1">
            {["day", "week", "month"].map((v) => (
              <button key={v} data-testid={`cal-view-${v}`} onClick={() => setView(v)} className={`px-4 py-2 rounded-full text-sm capitalize transition-colors duration-200 ${view === v ? "bg-[#D4AF37] text-black" : "text-gray-400 hover:text-white"}`}>{v}</button>
            ))}
          </div>
          <button data-testid="cal-new-booking" onClick={() => setShowManual(true)} className="px-4 py-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium flex items-center gap-1"><Plus className="w-4 h-4" /> New</button>
        </div>
      </div>

      {view === "day" && <CalDayList list={byDate[r.from] || []} />}

      {view === "week" && (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
          {[...Array(7)].map((_, i) => {
            const d = new Date(r.start); d.setDate(r.start.getDate() + i); const k = iso(d);
            return (
              <div key={k} className="bg-[#141414] border border-white/5 rounded-xl p-3 min-h-[140px]">
                <div className="label-caps mb-2">{d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric" })}</div>
                <div className="space-y-2">{(byDate[k] || []).map((a) => <CalChip key={a.id} a={a} />)}</div>
              </div>
            );
          })}
        </div>
      )}

      {view === "month" && (
        <div className="grid grid-cols-7 gap-2">
          {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => <div key={d} className="label-caps text-center pb-2">{d}</div>)}
          {(() => {
            const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
            const offset = (first.getDay() + 6) % 7;
            const days = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate();
            const cells = [];
            for (let i = 0; i < offset; i++) cells.push(<div key={`e${i}`} />);
            for (let day = 1; day <= days; day++) {
              const k = iso(new Date(anchor.getFullYear(), anchor.getMonth(), day));
              const list = byDate[k] || [];
              cells.push(
                <button key={k} onClick={() => { setAnchor(new Date(anchor.getFullYear(), anchor.getMonth(), day)); setView("day"); }}
                  className="bg-[#141414] border border-white/5 rounded-xl p-2 min-h-[84px] text-left hover:border-[#D4AF37]/40 transition-colors duration-200">
                  <div className="text-sm text-gray-400">{day}</div>
                  {list.length > 0 && <div className="mt-1 text-xs text-[#D4AF37] font-medium">{list.length} booking{list.length > 1 ? "s" : ""}</div>}
                  <div className="flex gap-1 mt-1 flex-wrap">{list.slice(0, 4).map((a) => <span key={a.id} className={`w-1.5 h-1.5 rounded-full ${(STATUS[a.status] || "").split(" ")[1]?.replace("text", "bg") || "bg-gray-500"}`} />)}</div>
                </button>
              );
            }
            return cells;
          })()}
        </div>
      )}
      {showManual && <ManualModal barbers={barbers} services={services} initialDate={iso(anchor)} onClose={() => setShowManual(false)} onDone={() => { setShowManual(false); setRefresh((x) => x + 1); }} />}
    </div>
  );
}

const CalChip = ({ a }) => (
  <div className={`rounded-lg px-2 py-1 border text-xs ${STATUS[a.status] || STATUS.cancelled}`}>
    <div className="font-medium">{a.start_time} {a.customer_name}</div>
    <div className="opacity-80">{a.service_name}</div>
  </div>
);
const CalDayList = ({ list }) => list.length === 0 ? <p className="text-gray-500 py-10 text-center">No bookings this day.</p> : (
  <div className="space-y-3">
    {list.map((a) => (
      <div key={a.id} className="bg-[#141414] border border-white/5 rounded-2xl p-5 flex items-center gap-4 flex-wrap">
        <div className="text-center w-16"><div className="font-display text-xl text-[#D4AF37]">{a.start_time}</div><div className="text-[0.6rem] text-gray-500">{a.duration}min</div></div>
        <div className="flex-1"><div className="flex items-center gap-2">{a.customer_name} <Badge s={a.status} /></div>
          <div className="text-sm text-gray-400">{a.service_name} · £{a.price}</div>
          <div className="text-xs text-gray-500 flex gap-3 mt-1">{a.customer_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{a.customer_phone}</span>}{a.notes && <span className="italic">"{a.notes}"</span>}</div>
        </div>
      </div>
    ))}
  </div>
);

// ---------------- Availability
const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
function Availability() {
  const [s, setS] = useState(null);
  const [holiday, setHoliday] = useState({ start: "", end: "", reason: "" });
  const [block, setBlock] = useState({ weekday: "monday", start: "12:00", end: "13:00", reason: "Lunch" });
  const loaded = useRef(false);
  const load = () => api.get("/settings").then((r) => { if (!loaded.current) { loaded.current = true; setS(r.data); } });
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      await api.put("/settings", { working_hours: s.working_hours, holidays: s.holidays, holiday_ranges: s.holiday_ranges, weekly_blocks: s.weekly_blocks, auto_confirm: s.auto_confirm, reminder_hours: s.reminder_hours, daily_digest: s.daily_digest, digest_time: s.digest_time, session_timeout_minutes: s.session_timeout_minutes, notify_owner_email: s.notify_owner_email, notify_inapp: s.notify_inapp, booking_reminders: s.booking_reminders });
      toast.success("Availability saved");
    } catch (e) { toast.error(apiError(e)); }
  };
  const setDay = (d, k, v) => setS({ ...s, working_hours: { ...s.working_hours, [d]: { ...s.working_hours[d], [k]: v } } });
  const addBreak = (d) => setDay(d, "breaks", [...(s.working_hours[d].breaks || []), { start: "12:00", end: "13:00" }]);
  const setBreak = (d, i, k, v) => { const b = [...(s.working_hours[d].breaks || [])]; b[i] = { ...b[i], [k]: v }; setDay(d, "breaks", b); };
  const rmBreak = (d, i) => setDay(d, "breaks", s.working_hours[d].breaks.filter((_, x) => x !== i));
  if (!s) return <Loader />;
  return (
    <div className="space-y-6">
      <div className="bg-[#141414] border border-white/5 rounded-2xl p-6 flex flex-wrap items-center gap-6">
        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" data-testid="auto-confirm" checked={!!s.auto_confirm} onChange={(e) => setS({ ...s, auto_confirm: e.target.checked })} /> <span>Auto-confirm bookings <span className="text-gray-500 text-sm">(off = new bookings are Pending until you accept)</span></span></label>
        <div className="flex items-center gap-2"><span className="label-caps">Reminder</span><input type="number" data-testid="reminder-hours" value={s.reminder_hours ?? 24} onChange={(e) => setS({ ...s, reminder_hours: Number(e.target.value) })} className="bg-[#0A0A0A] border border-white/10 rounded-lg px-2 py-1 w-16" /> hrs before</div>
      </div>

      <div className="bg-[#141414] border border-white/5 rounded-2xl p-6 flex flex-wrap items-center gap-6">
        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" data-testid="daily-digest-toggle" checked={!!s.daily_digest} onChange={(e) => setS({ ...s, daily_digest: e.target.checked })} /> <span>Daily booking digest <span className="text-gray-500 text-sm">(morning email listing today's confirmed appointments)</span></span></label>
        <div className="flex items-center gap-2"><span className="label-caps">Send at</span><input type="time" data-testid="digest-time" value={s.digest_time || "08:00"} onChange={(e) => setS({ ...s, digest_time: e.target.value })} className="bg-[#0A0A0A] border border-white/10 rounded-lg px-2 py-1" disabled={!s.daily_digest} /> <span className="text-gray-500 text-sm">UK time</span></div>
        <div className="flex items-center gap-2"><span className="label-caps">Auto sign-out</span><input type="number" min="5" data-testid="session-timeout" value={s.session_timeout_minutes ?? 30} onChange={(e) => setS({ ...s, session_timeout_minutes: Number(e.target.value) })} className="bg-[#0A0A0A] border border-white/10 rounded-lg px-2 py-1 w-16" /> min idle</div>
        <label className="flex items-center gap-2 cursor-pointer text-sm"><input type="checkbox" data-testid="notify-owner-email" checked={s.notify_owner_email !== false} onChange={(e) => setS({ ...s, notify_owner_email: e.target.checked })} /> Email me new bookings</label>
        <label className="flex items-center gap-2 cursor-pointer text-sm"><input type="checkbox" data-testid="notify-inapp" checked={s.notify_inapp !== false} onChange={(e) => setS({ ...s, notify_inapp: e.target.checked })} /> In-app alerts</label>
        <label className="flex items-center gap-2 cursor-pointer text-sm"><input type="checkbox" data-testid="booking-reminders-toggle" checked={s.booking_reminders !== false} onChange={(e) => setS({ ...s, booking_reminders: e.target.checked })} /> <span>Customer reminders <span className="text-gray-500">(email + in-app the day before each confirmed appointment)</span></span></label>
        <button data-testid="digest-save" onClick={save} className="px-5 py-2 rounded-full bg-white/5 text-gray-200 text-sm hover:bg-white/10 transition-colors">Save</button>
      </div>

      <div className="bg-[#141414] border border-white/5 rounded-2xl p-6 space-y-4">
        <h3 className="label-caps">Working Hours, Breaks & Appointment-Only Days</h3>
        {DAYS.map((d) => {
          const h = s.working_hours[d] || {};
          return (
            <div key={d} className="border-b border-white/5 pb-3">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="w-24 capitalize">{d}</span>
                <label className="flex items-center gap-1 text-sm text-gray-400"><input type="checkbox" checked={!h.closed} onChange={(e) => setDay(d, "closed", !e.target.checked)} /> Open</label>
                <input type="time" value={h.open || "09:00"} onChange={(e) => setDay(d, "open", e.target.value)} disabled={h.closed} className="bg-[#0A0A0A] border border-white/10 rounded-lg px-2 py-1 text-sm disabled:opacity-30" />
                <span className="text-gray-600">–</span>
                <input type="time" value={h.close || "18:00"} onChange={(e) => setDay(d, "close", e.target.value)} disabled={h.closed} className="bg-[#0A0A0A] border border-white/10 rounded-lg px-2 py-1 text-sm disabled:opacity-30" />
                <label className="flex items-center gap-1 text-sm text-[#D4AF37]"><input type="checkbox" checked={!!h.appointment_only} onChange={(e) => setDay(d, "appointment_only", e.target.checked)} /> Appt only</label>
                <button onClick={() => addBreak(d)} className="text-xs text-gray-400 hover:text-[#D4AF37] flex items-center gap-1"><Plus className="w-3 h-3" /> break</button>
              </div>
              {(h.breaks || []).map((br, i) => (
                <div key={i} className="flex items-center gap-2 ml-24 mt-2 text-sm text-gray-400">
                  <span>Break</span>
                  <input type="time" value={br.start} onChange={(e) => setBreak(d, i, "start", e.target.value)} className="bg-[#0A0A0A] border border-white/10 rounded-lg px-2 py-1" />
                  <span>–</span>
                  <input type="time" value={br.end} onChange={(e) => setBreak(d, i, "end", e.target.value)} className="bg-[#0A0A0A] border border-white/10 rounded-lg px-2 py-1" />
                  <button onClick={() => rmBreak(d, i)} className="text-gray-500 hover:text-red-400"><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          );
        })}
        <button data-testid="availability-save" onClick={save} className="px-8 py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#F3E5AB] transition-colors duration-300">Save Availability</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#141414] border border-white/5 rounded-2xl p-6">
          <h3 className="label-caps mb-4">Holidays / Closures (date range)</h3>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <input type="date" value={holiday.start} onChange={(e) => setHoliday({ ...holiday, start: e.target.value })} className={inputCls} data-testid="holiday-start" />
            <input type="date" value={holiday.end} onChange={(e) => setHoliday({ ...holiday, end: e.target.value })} className={inputCls} data-testid="holiday-end" />
          </div>
          <input placeholder="Reason (e.g. Annual leave)" value={holiday.reason} onChange={(e) => setHoliday({ ...holiday, reason: e.target.value })} className={inputCls + " mb-3"} />
          <button data-testid="holiday-add" onClick={() => { if (holiday.start && holiday.end) { setS({ ...s, holiday_ranges: [...(s.holiday_ranges || []), { id: Date.now().toString(), ...holiday }] }); setHoliday({ start: "", end: "", reason: "" }); } }} className="px-5 py-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium">Add Holiday</button>
          <div className="space-y-2 mt-4">
            {(s.holiday_ranges || []).map((h) => (
              <div key={h.id} className="flex justify-between items-center bg-[#0A0A0A] rounded-lg px-3 py-2 text-sm"><span>{h.start} → {h.end} <span className="text-gray-500">{h.reason}</span></span><button onClick={() => setS({ ...s, holiday_ranges: s.holiday_ranges.filter((x) => x.id !== h.id) })} className="text-gray-500 hover:text-red-400"><X className="w-4 h-4" /></button></div>
            ))}
            {(!s.holiday_ranges || s.holiday_ranges.length === 0) && <p className="text-gray-500 text-sm">None set.</p>}
          </div>
        </div>

        <div className="bg-[#141414] border border-white/5 rounded-2xl p-6">
          <h3 className="label-caps mb-4">Repeating Weekly Blocks</h3>
          <div className="flex flex-wrap gap-2 mb-3">
            <select value={block.weekday} onChange={(e) => setBlock({ ...block, weekday: e.target.value })} className={inputCls + " w-32"}>{DAYS.map((d) => <option key={d} value={d}>{d}</option>)}</select>
            <input type="time" value={block.start} onChange={(e) => setBlock({ ...block, start: e.target.value })} className={inputCls + " w-28"} />
            <input type="time" value={block.end} onChange={(e) => setBlock({ ...block, end: e.target.value })} className={inputCls + " w-28"} />
          </div>
          <input placeholder="Reason" value={block.reason} onChange={(e) => setBlock({ ...block, reason: e.target.value })} className={inputCls + " mb-3"} />
          <button onClick={() => setS({ ...s, weekly_blocks: [...(s.weekly_blocks || []), { id: Date.now().toString(), ...block }] })} className="px-5 py-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium">Add Block</button>
          <div className="space-y-2 mt-4">
            {(s.weekly_blocks || []).map((b) => (
              <div key={b.id} className="flex justify-between items-center bg-[#0A0A0A] rounded-lg px-3 py-2 text-sm"><span className="capitalize">{b.weekday} {b.start}–{b.end} <span className="text-gray-500">{b.reason}</span></span><button onClick={() => setS({ ...s, weekly_blocks: s.weekly_blocks.filter((x) => x.id !== b.id) })} className="text-gray-500 hover:text-red-400"><X className="w-4 h-4" /></button></div>
            ))}
            {(!s.weekly_blocks || s.weekly_blocks.length === 0) && <p className="text-gray-500 text-sm">None set.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------- Services
function Services() {
  const [items, setItems] = useState(null);
  const blank = { name: "", description: "", price: 10, duration: 30, category: "Haircut", photo: "", active: true, order: 0 };
  const [form, setForm] = useState(blank);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/services?all=true").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => { if (!form.name) return; try { await api.post("/services", { ...form, price: Number(form.price), duration: Number(form.duration) }); setForm(blank); load(); toast.success("Service added"); } catch (e) { toast.error(apiError(e)); } };
  const toggle = async (s) => { await api.put(`/services/${s.id}`, { ...s, active: !s.active }); load(); };
  const del = async (id) => { await api.delete(`/services/${id}`); load(); };
  const move = async (idx, dir) => {
    const arr = [...items]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setItems(arr);
    try { await api.put("/services-reorder", { ids: arr.map((x) => x.id) }); } catch (e) { toast.error(apiError(e)); load(); }
  };
  const uploadPhoto = async (e) => { const f = e.target.files[0]; if (!f) return; const fd = new FormData(); fd.append("file", f); try { const { data } = await api.post("/upload", fd); setForm({ ...form, photo: data.url }); toast.success("Photo uploaded"); } catch (err) { toast.error(apiError(err)); } };
  if (!items) return <Loader />;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-3">
        {items.map((s, idx) => (
          <div key={s.id} className="bg-[#141414] border border-white/5 rounded-xl p-5 flex justify-between items-center gap-3">
            <div className="flex items-center gap-3">
              <div className="flex flex-col">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`svc-up-${s.id}`} className="text-gray-500 hover:text-[#D4AF37] disabled:opacity-20"><ArrowUp className="w-4 h-4" /></button>
                <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} data-testid={`svc-down-${s.id}`} className="text-gray-500 hover:text-[#D4AF37] disabled:opacity-20"><ArrowDown className="w-4 h-4" /></button>
              </div>
              {s.photo && <img src={imgURL(s.photo)} alt="" className="w-12 h-12 rounded-lg object-cover" />}
              <div><div className="text-lg">{s.name} <span className="text-[#D4AF37]">£{s.price}</span></div><div className="text-sm text-gray-400">{s.category} · {s.duration}min</div></div>
            </div>
            <div className="flex items-center gap-3">
              <button data-testid={`edit-service-${s.id}`} onClick={() => setEdit(s)} className="text-gray-400 hover:text-[#D4AF37]"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => toggle(s)} data-testid={`toggle-service-${s.id}`} className={`text-xs px-3 py-1 rounded-full ${s.active ? "bg-green-500/10 text-green-400" : "bg-white/5 text-gray-500"}`}>{s.active ? "Active" : "Hidden"}</button>
              <button data-testid={`del-service-${s.id}`} onClick={() => del(s.id)} className="text-gray-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
      <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 space-y-3 h-fit">
        <h3 className="font-display text-2xl mb-2">Add Service</h3>
        <input data-testid="svc-name" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
        <input data-testid="svc-desc" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} />
        <div className="grid grid-cols-2 gap-3"><input data-testid="svc-price" type="number" placeholder="Price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls} /><input data-testid="svc-duration" type="number" placeholder="Mins" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className={inputCls} /></div>
        <input data-testid="svc-category" placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} />
        <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer"><Upload className="w-4 h-4" /> {form.photo ? "Photo added" : "Upload photo"}<input type="file" accept="image/*" onChange={uploadPhoto} className="hidden" /></label>
        <button data-testid="svc-add" onClick={add} className="w-full py-3 rounded-full bg-[#D4AF37] text-black font-medium flex items-center justify-center gap-2 hover:bg-[#F3E5AB] transition-colors duration-300"><Plus className="w-4 h-4" /> Add</button>
      </div>
      {edit && <ServiceEditModal s={edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function ServiceEditModal({ s, onClose, onDone }) {
  const [f, setF] = useState({ ...s });
  const save = async () => {
    try {
      await api.put(`/services/${s.id}`, { name: f.name, description: f.description || "", price: Number(f.price), duration: Number(f.duration), category: f.category, photo: f.photo || "", order: f.order || 0, active: f.active });
      toast.success("Service updated"); onDone();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Modal title="Edit Service" onClose={onClose}>
      <div className="space-y-3">
        <input data-testid="edit-svc-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} />
        <input value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} className={inputCls} />
        <div className="grid grid-cols-2 gap-3">
          <input data-testid="edit-svc-price" type="number" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className={inputCls} />
          <input data-testid="edit-svc-duration" type="number" value={f.duration} onChange={(e) => setF({ ...f, duration: e.target.value })} className={inputCls} />
        </div>
        <input value={f.category || ""} onChange={(e) => setF({ ...f, category: e.target.value })} className={inputCls} />
        <button data-testid="edit-svc-save" onClick={save} className="w-full py-3 rounded-full bg-[#D4AF37] text-black font-medium">Save Changes</button>
      </div>
    </Modal>
  );
}

// ---------------- Barbers management (simple profiles)
function BarbersManager() {
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = () => api.get("/barbers?all=true").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const toggle = async (b) => {
    try { await api.put(`/barbers/${b.id}`, { ...b, active: !b.active }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!items) return <Loader />;
  return (
    <div data-testid="barbers-manager">
      <div className="flex justify-between items-center mb-6 gap-4 flex-wrap">
        <p className="text-gray-400 text-sm">Manage the barbers shown on your public site.</p>
        <button data-testid="barber-add" onClick={() => setAdding(true)} className="px-5 py-2.5 rounded-full bg-[#D4AF37] text-black font-medium flex items-center gap-2 hover:bg-[#F3E5AB] transition-colors duration-300"><Plus className="w-4 h-4" /> Add Barber</button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((b) => (
          <div key={b.id} data-testid={`barber-row-${b.id}`} className="bg-[#141414] border border-white/5 rounded-2xl overflow-hidden">
            {b.photo ? <img src={imgURL(b.photo)} alt={b.name} className="w-full h-56 object-cover" /> : <div className="w-full h-56 bg-white/5 flex items-center justify-center"><UserCog className="w-10 h-10 text-gray-600" /></div>}
            <div className="p-5">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-display text-2xl">{b.name}</h3>
                <span className={`text-xs px-3 py-1 rounded-full shrink-0 ${b.active ? "bg-green-500/10 text-green-400" : "bg-white/5 text-gray-500"}`}>{b.active ? "Active" : "Inactive"}</span>
              </div>
              {b.bio && <p className="text-gray-400 text-sm mt-2 line-clamp-3">{b.bio}</p>}
              <div className="flex items-center gap-4 mt-4">
                <button data-testid={`barber-edit-${b.id}`} onClick={() => setEdit(b)} className="text-gray-300 hover:text-[#D4AF37] flex items-center gap-1 text-sm"><Pencil className="w-4 h-4" /> Edit</button>
                <button data-testid={`barber-toggle-${b.id}`} onClick={() => toggle(b)} className="text-gray-300 hover:text-[#D4AF37] flex items-center gap-1 text-sm">{b.active ? <><EyeOff className="w-4 h-4" /> Deactivate</> : <><Eye className="w-4 h-4" /> Activate</>}</button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {(adding || edit) && <BarberModal barber={edit} onClose={() => { setAdding(false); setEdit(null); }} onDone={() => { setAdding(false); setEdit(null); load(); }} />}
    </div>
  );
}

function BarberModal({ barber, onClose, onDone }) {
  const [f, setF] = useState(barber || { name: "", bio: "", photo: "", specialties: [], active: true });
  const [saving, setSaving] = useState(false);
  const uploadPhoto = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/upload", fd); setF((x) => ({ ...x, photo: data.url })); toast.success("Photo uploaded"); }
    catch (err) { toast.error(apiError(err)); }
  };
  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    const payload = {
      name: f.name.trim(), bio: f.bio || "", photo: f.photo || "",
      specialties: Array.isArray(f.specialties) ? f.specialties : (f.specialties ? String(f.specialties).split(",").map((x) => x.trim()) : []),
      working_hours: f.working_hours || {}, active: f.active,
    };
    try {
      if (barber) await api.put(`/barbers/${barber.id}`, payload);
      else await api.post("/barbers", payload);
      toast.success(barber ? "Barber updated" : "Barber added"); onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Modal title={barber ? "Edit Barber" : "Add Barber"} onClose={onClose}>
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          {f.photo ? <img src={imgURL(f.photo)} alt="" className="w-20 h-20 rounded-xl object-cover" /> : <div className="w-20 h-20 rounded-xl bg-white/5 flex items-center justify-center"><UserCog className="w-7 h-7 text-gray-600" /></div>}
          <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3 hover:border-[#D4AF37]/40"><Upload className="w-4 h-4" /> {f.photo ? "Change photo" : "Upload photo"}<input type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="barber-photo-input" /></label>
        </div>
        <div><label className="label-caps block mb-1">Name</label><input data-testid="barber-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} /></div>
        <div><label className="label-caps block mb-1">Short biography (optional)</label><textarea data-testid="barber-bio" value={f.bio || ""} onChange={(e) => setF({ ...f, bio: e.target.value })} rows={3} className={inputCls} /></div>
        <label className="flex items-center gap-2 text-sm text-gray-300"><input type="checkbox" data-testid="barber-active" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} className="accent-[#D4AF37]" /> Active (visible on site)</label>
        <button data-testid="barber-save" onClick={save} disabled={saving} className="w-full py-3 rounded-full bg-[#D4AF37] text-black font-medium disabled:opacity-60 flex items-center justify-center gap-2">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (barber ? "Save Changes" : "Add Barber")}</button>
      </div>
    </Modal>
  );
}

// ---------------- Customers
function Customers() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [flagFor, setFlagFor] = useState(null);
  const load = () => api.get("/customers", { params: q ? { q } : {} }).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]); // eslint-disable-line
  const open = async (cid) => { const { data } = await api.get(`/customers/${cid}`); setDetail(data); };
  const addNote = async () => { if (!note.trim()) return; await api.post("/notes", { customer_id: detail.customer.id, text: note }); setNote(""); open(detail.customer.id); toast.success("Note added"); };
  const doFlag = async (c, val, reason = "") => { try { await api.put(`/customers/${c.id}/flag`, { flagged_no_show: val, no_show_reason: reason }); toast.success(val ? "Marked as repeat no-show — future requests stay Pending" : "No-show flag removed"); load(); if (detail && detail.customer.id === c.id) open(c.id); } catch (e) { toast.error(apiError(e)); } };
  if (!items) return <Loader />;
  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <input data-testid="customer-search" placeholder="Search by name, email or phone" value={q} onChange={(e) => setQ(e.target.value)} className={inputCls + " max-w-md"} />
        <button data-testid="export-customers" onClick={() => downloadCsv("/customers/export", `customers_${new Date().toISOString().slice(0, 10)}.csv`)} className="px-5 py-3 rounded-full border border-white/15 text-gray-200 flex items-center gap-2 hover:border-[#D4AF37]/50 transition-colors duration-200 shrink-0"><FileText className="w-4 h-4" /> Export CSV</button>
      </div>
      {items.length === 0 ? <p className="text-gray-500">No customers.</p> : (
        <div className="bg-[#141414] border border-white/5 rounded-2xl overflow-hidden overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead><tr className="border-b border-white/10 text-gray-500 text-xs uppercase"><th className="p-4">Name</th><th className="p-4">Phone</th><th className="p-4">Email</th><th className="p-4">Visits</th><th className="p-4">No-shows</th><th className="p-4">Upcoming</th><th className="p-4"></th></tr></thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} data-testid={`customer-row-${c.id}`} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-4"><div className="flex items-center gap-2">{c.name}{c.flagged_no_show && <span data-testid={`flag-badge-${c.id}`} title={c.no_show_reason || "Repeat no-show"} className="text-[0.6rem] px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/30 uppercase tracking-wide">No-show</span>}</div></td>
                  <td className="p-4 text-gray-400">{c.phone || "—"}</td><td className="p-4 text-gray-400">{c.email}</td>
                  <td className="p-4">{c.visits}</td><td className="p-4">{c.no_shows}</td>
                  <td className="p-4 text-gray-400">{c.upcoming ? `${ddmm(c.upcoming.date)} ${c.upcoming.start_time}` : "—"}</td>
                  <td className="p-4 whitespace-nowrap">
                    <button data-testid={`flag-${c.id}`} onClick={() => (c.flagged_no_show ? doFlag(c, false) : setFlagFor(c))} className={`text-xs mr-3 hover:underline ${c.flagged_no_show ? "text-gray-400" : "text-red-400"}`}>{c.flagged_no_show ? "Unflag" : "Flag no-show"}</button>
                    <button data-testid={`customer-view-${c.id}`} onClick={() => open(c.id)} className="text-[#D4AF37] hover:underline">View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {flagFor && <FlagModal customer={flagFor} onClose={() => setFlagFor(null)} onDone={(reason) => { doFlag(flagFor, true, reason); setFlagFor(null); }} />}
      {detail && (
        <Modal title={detail.customer.name} onClose={() => setDetail(null)}>
          <div className="space-y-2 text-sm text-gray-400">
            <div>{detail.customer.email} · {detail.customer.phone || "no phone"}</div>
          </div>
          <div className={`mt-4 rounded-xl p-3 border ${detail.customer.flagged_no_show ? "bg-red-500/10 border-red-500/30" : "bg-[#0A0A0A] border-white/10"}`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium">{detail.customer.flagged_no_show ? "Flagged as repeat no-show" : "Repeat no-show flag"}</div>
                {detail.customer.flagged_no_show && detail.customer.no_show_reason && <div className="text-xs text-red-300 mt-0.5" data-testid="detail-flag-reason">Reason: {detail.customer.no_show_reason}</div>}
                <div className="text-xs text-gray-500 mt-1">When flagged, this customer's booking requests are still saved but always stay Pending for your manual approval.</div>
              </div>
              <button data-testid={`detail-flag-${detail.customer.id}`} onClick={() => (detail.customer.flagged_no_show ? doFlag(detail.customer, false) : setFlagFor(detail.customer))} className={`shrink-0 px-4 py-2 rounded-full text-sm ${detail.customer.flagged_no_show ? "bg-white/5 text-gray-300 hover:bg-white/10" : "bg-red-500/15 text-red-400 hover:bg-red-500/25"}`}>{detail.customer.flagged_no_show ? "Remove flag" : "Flag no-show"}</button>
            </div>
          </div>
          <div className="mt-4"><div className="label-caps mb-2">Booking History</div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {detail.appointments.length === 0 ? <p className="text-gray-500 text-sm">None.</p> : detail.appointments.map((a) => (
                <div key={a.id} className="flex justify-between text-sm bg-[#0A0A0A] rounded-lg px-3 py-2"><span>{ddmm(a.date)} {a.start_time} · {a.service_name}</span><Badge s={a.status} /></div>
              ))}
            </div>
          </div>
          <div className="mt-4"><div className="label-caps mb-2">Admin Notes</div>
            <div className="flex gap-2 mb-2"><input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add note" className={inputCls} data-testid="customer-note-input" /><button onClick={addNote} className="px-4 rounded-xl bg-[#D4AF37] text-black"><Plus className="w-4 h-4" /></button></div>
            <div className="space-y-2 max-h-32 overflow-y-auto">{detail.notes.map((n) => <div key={n.id} className="text-sm bg-[#0A0A0A] rounded-lg px-3 py-2 text-gray-300">{n.text}</div>)}</div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function FlagModal({ customer, onClose, onDone }) {
  const [reason, setReason] = useState("");
  return (
    <Modal title={`Flag ${customer.name}`} onClose={onClose}>
      <p className="text-sm text-gray-400 mb-4">Their future booking requests will still be saved but always stay <span className="text-amber-400">Pending</span> for your manual approval. Add a short reason (optional) — it shows next to their requests.</p>
      <textarea data-testid="flag-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={140} placeholder="e.g. Missed last 2 appointments without notice" className={inputCls + " mb-4"} />
      <div className="flex gap-3 justify-end">
        <button onClick={onClose} className="px-5 py-2 rounded-full bg-white/5 text-gray-300 text-sm hover:bg-white/10">Cancel</button>
        <button data-testid="flag-confirm-btn" onClick={() => onDone(reason.trim())} className="px-5 py-2 rounded-full bg-red-500/15 text-red-400 text-sm hover:bg-red-500/25">Flag as No-show</button>
      </div>
    </Modal>
  );
}


// ---------------- Reviews
function Reviews() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/reviews/all").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const approve = async (id, val) => { await api.put(`/reviews/${id}/approve`, { approved: val }); load(); };
  const del = async (id) => { await api.delete(`/reviews/${id}`); load(); };
  if (!items) return <Loader />;
  return (
    <div className="space-y-3">
      {items.map((r) => (
        <div key={r.id} data-testid={`admin-review-${r.id}`} className="bg-[#141414] border border-white/5 rounded-xl p-5 flex justify-between gap-4">
          <div><div className="flex gap-1 mb-2">{[...Array(r.rating)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-[#D4AF37] text-[#D4AF37]" />)}</div><p className="text-gray-300 italic">"{r.text}"</p><div className="text-xs text-gray-500 mt-2">{r.author} {r.approved ? <span className="text-green-400">· published</span> : <span className="text-yellow-400">· pending</span>}</div></div>
          <div className="flex items-center gap-2 shrink-0">
            {!r.approved && <button data-testid={`approve-${r.id}`} onClick={() => approve(r.id, true)} className="p-2 rounded-full bg-green-500/10 text-green-400"><Check className="w-4 h-4" /></button>}
            {r.approved && <button onClick={() => approve(r.id, false)} className="p-2 rounded-full bg-white/5 text-gray-400"><X className="w-4 h-4" /></button>}
            <button onClick={() => del(r.id)} className="p-2 rounded-full bg-white/5 text-gray-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>
      ))}
      {items.length === 0 && <p className="text-gray-500">No reviews yet.</p>}
    </div>
  );
}

// ---------------- Gallery
function GalleryAdmin() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ url: "", title: "", category: "Skin Fade" });
  const [uploading, setUploading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const dragIdx = useRef(null);
  const load = () => { api.get("/gallery?all=true").then((r) => { setItems(r.data); setDirty(false); }); };
  useEffect(() => { load(); }, []);
  const upload = async (e) => { const f = e.target.files[0]; if (!f) return; setUploading(true); const fd = new FormData(); fd.append("file", f); try { const { data } = await api.post("/upload", fd); setForm({ ...form, url: data.url }); toast.success("Uploaded — add details & save"); } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); } };
  const add = async () => { if (!form.url) return toast.error("Upload or paste an image URL first"); try { await api.post("/gallery", form); setForm({ url: "", title: "", category: "Skin Fade" }); load(); toast.success("Added to gallery"); } catch (e) { toast.error(apiError(e)); } };
  const upd = async (g, patch) => { await api.put(`/gallery/${g.id}`, patch); load(); };
  const del = async (id) => { await api.delete(`/gallery/${id}`); load(); };

  const onDrop = (i) => {
    const from = dragIdx.current;
    if (from === null || from === i) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(i, 0, moved);
    setItems(next);
    setDirty(true);
    dragIdx.current = null;
  };
  const saveOrder = async () => { try { await api.put("/gallery-reorder", { ids: items.map((g) => g.id) }); toast.success("Gallery order saved"); setDirty(false); } catch (e) { toast.error(apiError(e)); } };

  if (!items) return <Loader />;
  return (
    <div>
      <div className="bg-[#141414] border border-white/10 rounded-2xl p-5 flex flex-col sm:flex-row gap-3 mb-6 items-center">
        <label className="flex items-center gap-2 text-sm cursor-pointer bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3 shrink-0 hover:border-[#D4AF37]/40" data-testid="gallery-upload">
          <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : form.url ? "Image ready" : "Upload image"}
          <input type="file" accept="image/*" onChange={upload} className="hidden" />
        </label>
        <input data-testid="gallery-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls + " sm:w-44"}>{["Skin Fade", "Beard", "Classic Cut", "Kids Cut", "Interior"].map((c) => <option key={c}>{c}</option>)}</select>
        <button data-testid="gallery-add" onClick={add} className="px-6 rounded-full bg-[#D4AF37] text-black font-medium shrink-0 py-3 flex items-center gap-2"><Plus className="w-4 h-4" /> Add</button>
      </div>

      <div className="flex items-center justify-between mb-4">
        <p className="text-gray-500 text-sm">Drag photos to reorder — the public gallery follows this order.</p>
        <button data-testid="gallery-save-order" onClick={saveOrder} disabled={!dirty} className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${dirty ? "bg-[#D4AF37] text-black hover:bg-[#F3E5AB]" : "bg-white/5 text-gray-500 cursor-not-allowed"}`}>Save Order</button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
        {items.map((g, i) => (
          <div
            key={g.id}
            data-testid={`gallery-item-${g.id}`}
            draggable
            onDragStart={() => { dragIdx.current = i; }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(i)}
            className="relative group rounded-xl overflow-hidden bg-[#141414] border border-white/5 cursor-move"
          >
            <div className="absolute top-2 left-2 z-10 p-1 rounded-md bg-black/60 text-white/70 opacity-0 group-hover:opacity-100 transition-opacity"><GripVertical className="w-3.5 h-3.5" /></div>
            <img src={imgURL(g.url)} alt={g.title} className={`w-full h-40 object-cover pointer-events-none ${g.published ? "" : "opacity-40"}`} />
            <div className="p-2 text-xs"><div className="truncate">{g.title || "Untitled"}</div><div className="text-gray-500">{g.category}</div></div>
            <div className="absolute top-2 right-2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <button title="Featured on homepage" onClick={() => upd(g, { featured: !g.featured })} className={`p-1.5 rounded-full ${g.featured ? "bg-[#D4AF37] text-black" : "bg-black/70 text-white"}`}><Star className="w-3.5 h-3.5" /></button>
              <button title="Publish/Hide" onClick={() => upd(g, { published: !g.published })} className="p-1.5 rounded-full bg-black/70 text-white">{g.published ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}</button>
              <button data-testid={`del-gallery-${g.id}`} onClick={() => del(g.id)} className="p-1.5 rounded-full bg-black/70 text-white hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------- Content
function Content() {
  const { refreshShop } = useAuth();
  const [s, setS] = useState(null);
  const loaded = useRef(false);
  useEffect(() => { api.get("/settings").then((r) => { if (!loaded.current) { loaded.current = true; setS(r.data); } }); }, []);
  const setShop = (k, v) => setS({ ...s, shop: { ...s.shop, [k]: v } });
  const save = async () => { try { await api.put("/settings", { shop: s.shop, owner_email: s.owner_email }); if (refreshShop) await refreshShop(); toast.success("Website content saved"); } catch (e) { toast.error(apiError(e)); } };
  if (!s) return <Loader />;
  const F = ({ label, k, area, testid }) => (
    <div><label className="label-caps block mb-1">{label}</label>
      {area ? <textarea data-testid={testid} value={s.shop[k] || ""} onChange={(e) => setShop(k, e.target.value)} rows={3} className={inputCls} />
        : <input data-testid={testid} value={s.shop[k] || ""} onChange={(e) => setShop(k, e.target.value)} className={inputCls} />}
    </div>
  );
  return (
    <div className="max-w-3xl space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <F label="Business Name" k="name" /><F label="Phone" k="phone" testid="content-phone" />
        <F label="WhatsApp (intl, e.g. 447...)" k="whatsapp" /><F label="Address" k="address" />
        <div><label className="label-caps block mb-1">Owner Email (booking alerts)</label><input value={s.owner_email || ""} onChange={(e) => setS({ ...s, owner_email: e.target.value })} className={inputCls} data-testid="owner-email" /></div>
        <F label="Google Maps Link" k="map_link" />
        <F label="Google Maps Embed URL (map iframe)" k="map_embed" />
      </div>
      <F label="Homepage Heading" k="hero_heading" />
      <F label="Homepage Subheading" k="hero_sub" area />
      <F label="About Section" k="about" area />
      <F label="Appointment Policy" k="appointment_policy" area />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <F label="Instagram URL" k="instagram" /><F label="Facebook URL" k="facebook" /><F label="TikTok URL" k="tiktok" />
      </div>
      <button data-testid="content-save" onClick={save} className="px-8 py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#F3E5AB] transition-colors duration-300">Save Content</button>
      <ChangePassword />
    </div>
  );
}

function ChangePassword() {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (nw !== confirm) return toast.error("New passwords do not match");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated"); setCur(""); setNw(""); setConfirm("");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 space-y-3 mt-8" data-testid="change-password-card">
      <h3 className="label-caps flex items-center gap-2"><KeyRound className="w-4 h-4 text-[#D4AF37]" /> Change Password</h3>
      <p className="text-xs text-gray-500">Choose a new password. You'll keep using it until you change it again.</p>
      <div className="relative">
        <input type={show ? "text" : "password"} data-testid="cp-current" placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} className={inputCls + " pr-12"} />
        <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#D4AF37]">{show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
      </div>
      <input type={show ? "text" : "password"} data-testid="cp-new" placeholder="New password (min 8, incl. a letter & a number)" value={nw} onChange={(e) => setNw(e.target.value)} className={inputCls} />
      <input type={show ? "text" : "password"} data-testid="cp-confirm" placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} />
      <button data-testid="cp-save" onClick={submit} disabled={saving} className="px-6 py-3 rounded-full bg-[#D4AF37] text-black font-medium disabled:opacity-50">{saving ? "Saving…" : "Update Password"}</button>
    </div>
  );
}

function TodaysAppointments() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    api.get("/appointments", { params: { date_from: today, date_to: today } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  if (!items) return null;
  const sorted = items.filter((a) => !["cancelled", "declined"].includes(a.status)).sort((a, b) => a.start_time.localeCompare(b.start_time));
  return (
    <div className="bg-[#141414] border border-white/5 rounded-2xl p-6" data-testid="todays-appointments">
      <h3 className="label-caps mb-4">Today's Appointments</h3>
      {sorted.length === 0 ? <p className="text-gray-500 text-sm">No appointments today.</p> : (
        <div className="space-y-2">
          {sorted.map((a) => (
            <div key={a.id} data-testid={`today-appt-${a.id}`} className="flex items-center justify-between gap-3 flex-wrap border-b border-white/5 pb-2 last:border-0">
              <div className="flex items-center gap-3">
                <span className="font-display text-lg text-[#D4AF37] w-14 shrink-0">{a.start_time}</span>
                <div>
                  <div className="text-sm">{a.customer_name} <span className="text-gray-500">· {a.customer_phone || "—"}</span></div>
                  <div className="text-xs text-gray-400">{a.service_name}</div>
                </div>
              </div>
              <Badge s={a.status} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RecentRequests() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/appointments", { params: { status: "pending" } }).then((r) => setItems(r.data.slice(0, 6))).catch(() => {});
  useEffect(() => { load(); }, []);
  const act = async (id, status) => { try { await api.put(`/appointments/${id}/status`, { status }); toast.success(`Marked ${status}`); load(); } catch (e) { toast.error(apiError(e)); } };
  if (items.length === 0) return null;
  return (
    <div className="bg-[#141414] border border-white/5 rounded-2xl p-6" data-testid="recent-requests">
      <h3 className="label-caps mb-4">Recent Booking Requests</h3>
      <div className="space-y-3">
        {items.map((a) => (
          <div key={a.id} className="flex items-center justify-between gap-3 flex-wrap border-b border-white/5 pb-3 last:border-0">
            <div><div className="text-sm">{a.customer_name} · <span className="text-gray-400">{a.service_name}</span></div><div className="text-xs text-gray-500">{ddmm(a.date)} at {a.start_time}{a.customer_phone ? ` · ${a.customer_phone}` : ""}</div></div>
            <div className="flex gap-2">
              <button data-testid={`recent-accept-${a.id}`} onClick={() => act(a.id, "confirmed")} className="px-3 py-1.5 rounded-full text-xs bg-green-500/10 text-green-400 hover:bg-green-500/20">Accept</button>
              <button data-testid={`recent-reject-${a.id}`} onClick={() => act(a.id, "declined")} className="px-3 py-1.5 rounded-full text-xs bg-red-500/10 text-red-400 hover:bg-red-500/20">Reject</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NoShowModal({ appt, onClose, onDone }) {
  const [reason, setReason] = useState("");
  return <Modal title="Mark as No-show" onClose={onClose}>
    <p className="text-sm text-gray-400 mb-4">Add a short reason (optional). It's saved to the booking and its change history.</p>
    <textarea data-testid="noshow-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="e.g. Didn't turn up, no contact" className={inputCls + " mb-4"} />
    <button data-testid="noshow-confirm" onClick={() => onDone(reason.trim())} className="w-full py-3 rounded-full bg-rose-500/15 text-rose-400 hover:bg-rose-500/25">Mark No-show</button>
  </Modal>;
}

function Holidays() {
  const [s, setS] = useState(null);
  const rid = () => Math.random().toString(36).slice(2);
  const emptyFull = { name: "", start: "", end: "", reason: "", internal_note: "" };
  const emptyBlock = { date: "", start_time: "12:00", end_time: "13:00", reason: "", internal_note: "" };
  const [full, setFull] = useState(emptyFull);
  const [block, setBlock] = useState(emptyBlock);
  const [conflicts, setConflicts] = useState([]);
  const load = () => api.get("/settings").then((r) => setS(r.data));
  useEffect(() => { load(); }, []);
  const ranges = s?.holiday_ranges || [];
  const persist = async (next) => {
    try { await api.put("/settings", { holiday_ranges: next }); setS((p) => ({ ...p, holiday_ranges: next })); toast.success("Saved"); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => {
    if (!full.start || !full.end) { setConflicts([]); return; }
    api.get("/holidays/conflicts", { params: { start: full.start, end: full.end } }).then((r) => setConflicts(r.data)).catch(() => {});
  }, [full.start, full.end]); // eslint-disable-line
  const addFull = async () => {
    if (!full.start || !full.end) return toast.error("Choose start and end dates");
    if (full.end < full.start) return toast.error("End date must be on or after start date");
    await persist([...ranges, { id: rid(), all_day: true, ...full }]);
    setFull(emptyFull); setConflicts([]);
  };
  const addBlock = async () => {
    if (!block.date) return toast.error("Choose a date");
    if (block.end_time <= block.start_time) return toast.error("End time must be after start time");
    await persist([...ranges, { id: rid(), all_day: false, start: block.date, end: block.date, start_time: block.start_time, end_time: block.end_time, reason: block.reason, internal_note: block.internal_note }]);
    setBlock(emptyBlock);
  };
  const del = async (key) => { await persist(ranges.filter((r) => (r.id || r.start + r.end) !== key)); };
  if (!s) return <Loader />;
  const today = new Date().toISOString().split("T")[0];
  const future = ranges.filter((r) => (r.end || "") >= today);
  const past = ranges.filter((r) => (r.end || "") < today);
  const Row = ({ r }) => (
    <div className="flex items-center justify-between gap-3 bg-[#0A0A0A] border border-white/10 rounded-xl p-3" data-testid={`holiday-${r.id || r.start}`}>
      <div>
        <div className="text-sm flex items-center gap-2">
          {r.all_day === false
            ? <span className="text-[0.6rem] px-2 py-0.5 rounded-full bg-amber-400/10 text-amber-400 uppercase tracking-wide">Time block</span>
            : <span className="text-[0.6rem] px-2 py-0.5 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] uppercase tracking-wide">Full day</span>}
          {r.name && <span className="text-white">{r.name}</span>}
        </div>
        <div className="text-sm mt-1">
          {r.all_day === false
            ? <span>{ddmm(r.start)} · <span className="text-amber-400">{r.start_time}–{r.end_time}</span></span>
            : <span>{ddmm(r.start)} → {ddmm(r.end)} <span className="text-gray-500">(all day)</span></span>}
        </div>
        {r.reason && <div className="text-xs text-gray-400">{r.reason}</div>}
        {r.internal_note && <div className="text-[0.65rem] text-gray-600">Internal: {r.internal_note}</div>}
      </div>
      <button data-testid={`del-holiday-${r.id || r.start}`} onClick={() => del(r.id || r.start + r.end)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
    </div>
  );
  return (
    <div className="space-y-6" data-testid="holidays-page">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Full-Day Holiday */}
        <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 space-y-3" data-testid="full-day-form">
          <h3 className="label-caps flex items-center gap-2"><CalIcon className="w-4 h-4 text-[#D4AF37]" /> Add Full-Day Holiday</h3>
          <p className="text-xs text-gray-500">Barber completely unavailable — the whole day is closed for these dates.</p>
          <div><label className="label-caps block mb-1">Holiday name (optional)</label><input data-testid="holiday-full-name" placeholder="e.g. Eid Holiday, Annual Leave" value={full.name} onChange={(e) => setFull({ ...full, name: e.target.value })} className={inputCls} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label-caps block mb-1">Start date</label><input type="date" data-testid="holiday-full-start" value={full.start} onChange={(e) => setFull({ ...full, start: e.target.value })} className={inputCls} /></div>
            <div><label className="label-caps block mb-1">End date</label><input type="date" data-testid="holiday-full-end" value={full.end} onChange={(e) => setFull({ ...full, end: e.target.value })} className={inputCls} /></div>
          </div>
          <input data-testid="holiday-full-reason" placeholder="Reason (e.g. Shop Closed, Personal Leave)" value={full.reason} onChange={(e) => setFull({ ...full, reason: e.target.value })} className={inputCls} />
          <input data-testid="holiday-full-note" placeholder="Internal note (optional)" value={full.internal_note} onChange={(e) => setFull({ ...full, internal_note: e.target.value })} className={inputCls} />
          {conflicts.length > 0 && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3" data-testid="holiday-conflicts">
              <div className="flex items-center gap-2 text-red-400 text-sm mb-2"><AlertTriangle className="w-4 h-4" /> {conflicts.length} existing booking(s) fall within these dates. Reschedule or cancel them from Bookings — they are never removed automatically.</div>
              <div className="space-y-1 max-h-32 overflow-y-auto text-xs text-gray-300">
                {conflicts.map((c) => <div key={c.id}>{ddmm(c.date)} {c.start_time} · {c.customer_name} · {c.service_name} <span className="text-gray-500">({c.status})</span></div>)}
              </div>
            </div>
          )}
          <button data-testid="holiday-full-add" onClick={addFull} className="px-6 py-3 rounded-full bg-[#D4AF37] text-black font-medium flex items-center gap-2 hover:bg-[#F3E5AB] transition-colors duration-300"><Plus className="w-4 h-4" /> Add Full-Day Holiday</button>
        </div>

        {/* Specific Time Block */}
        <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 space-y-3" data-testid="time-block-form">
          <h3 className="label-caps flex items-center gap-2"><Clock className="w-4 h-4 text-amber-400" /> Add Specific Time Block</h3>
          <p className="text-xs text-gray-500">Barber unavailable for only part of a day. The rest of that day stays bookable.</p>
          <div><label className="label-caps block mb-1">Date</label><input type="date" data-testid="holiday-block-date" value={block.date} onChange={(e) => setBlock({ ...block, date: e.target.value })} className={inputCls} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label-caps block mb-1">Start time</label><input type="time" data-testid="holiday-block-from" value={block.start_time} onChange={(e) => setBlock({ ...block, start_time: e.target.value })} className={inputCls} /></div>
            <div><label className="label-caps block mb-1">End time</label><input type="time" data-testid="holiday-block-to" value={block.end_time} onChange={(e) => setBlock({ ...block, end_time: e.target.value })} className={inputCls} /></div>
          </div>
          <input data-testid="holiday-block-reason" placeholder="Reason (e.g. Dentist appointment)" value={block.reason} onChange={(e) => setBlock({ ...block, reason: e.target.value })} className={inputCls} />
          <input data-testid="holiday-block-note" placeholder="Internal note (optional)" value={block.internal_note} onChange={(e) => setBlock({ ...block, internal_note: e.target.value })} className={inputCls} />
          <button data-testid="holiday-block-add" onClick={addBlock} className="px-6 py-3 rounded-full bg-amber-400 text-black font-medium flex items-center gap-2 hover:bg-amber-300 transition-colors duration-300"><Plus className="w-4 h-4" /> Add Specific Time Block</button>
        </div>
      </div>
      <div><h3 className="label-caps mb-3">Upcoming</h3><div className="space-y-2">{future.length === 0 ? <p className="text-gray-500 text-sm">No upcoming holidays or time blocks.</p> : future.map((r) => <Row key={r.id || r.start} r={r} />)}</div></div>
      {past.length > 0 && <div><h3 className="label-caps mb-3">Previous</h3><div className="space-y-2 opacity-60">{past.map((r) => <Row key={r.id || r.start} r={r} />)}</div></div>}
    </div>
  );
}

// ---------------- Coupons
function Coupons() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ code: "", percent_off: 10, active: true });
  const load = () => api.get("/coupons").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const add = async () => { if (!form.code) return; try { await api.post("/coupons", { ...form, percent_off: Number(form.percent_off) }); setForm({ code: "", percent_off: 10, active: true }); load(); toast.success("Coupon created"); } catch (e) { toast.error(apiError(e)); } };
  const del = async (id) => { await api.delete(`/coupons/${id}`); load(); };
  if (!items) return <Loader />;
  return (
    <div className="max-w-xl">
      <div className="bg-[#141414] border border-white/10 rounded-2xl p-5 flex gap-3 mb-6">
        <input data-testid="coupon-code" placeholder="CODE" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className={inputCls} />
        <input data-testid="coupon-percent" type="number" placeholder="% off" value={form.percent_off} onChange={(e) => setForm({ ...form, percent_off: e.target.value })} className={inputCls + " w-28"} />
        <button data-testid="coupon-add" onClick={add} className="px-6 rounded-full bg-[#D4AF37] text-black font-medium shrink-0"><Plus className="w-4 h-4" /></button>
      </div>
      <div className="space-y-3">
        {items.map((c) => (
          <div key={c.id} className="bg-[#141414] border border-white/5 rounded-xl p-4 flex justify-between items-center"><div><span className="font-display text-xl text-[#D4AF37]">{c.code}</span> <span className="text-gray-400 ml-3">{c.percent_off}% off</span></div><button onClick={() => del(c.id)} className="text-gray-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button></div>
        ))}
        {items.length === 0 && <p className="text-gray-500">No coupons yet.</p>}
      </div>
    </div>
  );
}
